import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Banknote, ShieldCheck, Smartphone } from "lucide-react";

function fmt(n) {
    return new Intl.NumberFormat("en-KE", { maximumFractionDigits: 2 }).format(n || 0);
}

const PRODUCTS = [
    { v: "float", l: "Float financing", desc: "Working capital to keep selling VAS daily.", icon: Banknote },
    { v: "inventory", l: "Inventory financing", desc: "Buy stock now, repay after sales.", icon: ShieldCheck },
    { v: "device", l: "Device financing", desc: "POS, smartphone or solar with monthly repayments.", icon: Smartphone },
];

export default function CreditTab() {
    const [apps, setApps] = useState([]);
    const [product, setProduct] = useState("float");
    const [amount, setAmount] = useState(2000);
    const [tenor, setTenor] = useState(30);
    const [purpose, setPurpose] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        const { data } = await api.get("/credit/applications");
        setApps(data);
    };

    useEffect(() => { load(); }, []);

    const apply = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/credit/apply", {
                product, amount: Number(amount), tenor_days: Number(tenor), purpose,
            });
            if (data.application.status === "approved") {
                toast.success(`Approved! KES ${fmt(data.application.amount)} disbursed to your wallet.`);
            } else {
                toast.message("Application received and under review.");
            }
            await load();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="credit-page">
            <div>
                <h2 className="font-display text-3xl font-extrabold text-onego-navy">Merchant credit</h2>
                <p className="text-slate-600">Float, inventory or device credit underwritten on your transactions.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <form onSubmit={apply} className="og-card p-7" data-testid="credit-form">
                    <h3 className="font-display text-2xl font-bold text-onego-navy">Apply now</h3>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        {PRODUCTS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <button
                                    type="button"
                                    key={p.v}
                                    onClick={() => setProduct(p.v)}
                                    className={`p-4 rounded-2xl border text-left transition ${product === p.v ? "border-pink-500 bg-pink-50/40" : "border-slate-200 hover:border-onego-navy"}`}
                                    data-testid={`credit-product-${p.v}`}
                                >
                                    <Icon size={18} className="text-onego-navy" />
                                    <p className="mt-2 font-bold text-onego-navy">{p.l}</p>
                                    <p className="text-xs text-slate-500">{p.desc}</p>
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Amount (KES)</label>
                            <input className="og-input mt-1" type="number" min={500} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credit-amount-input" />
                        </div>
                        <div>
                            <label className="text-sm font-semibold text-onego-navy">Tenor (days)</label>
                            <select className="og-input mt-1" value={tenor} onChange={(e) => setTenor(e.target.value)} data-testid="credit-tenor-select">
                                {[7, 14, 30, 60, 90, 120, 180].map((d) => <option key={d} value={d}>{d} days</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="mt-4">
                        <label className="text-sm font-semibold text-onego-navy">Purpose</label>
                        <input className="og-input mt-1" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Buy stock for the weekend" data-testid="credit-purpose-input" />
                    </div>

                    <button type="submit" disabled={loading} className="og-btn-gradient mt-6 disabled:opacity-60" data-testid="credit-submit-btn">
                        {loading ? "Scoring\u2026" : "Submit application"}
                    </button>
                </form>

                <div className="og-card p-7" data-testid="credit-applications">
                    <h3 className="font-display text-2xl font-bold text-onego-navy">Your applications</h3>
                    <div className="mt-3 divide-y divide-slate-100">
                        {apps.length === 0 && <p className="py-4 text-sm text-slate-500">No applications yet.</p>}
                        {apps.map((a) => (
                            <div key={a.id} className="py-4">
                                <div className="flex items-center justify-between">
                                    <p className="font-bold text-onego-navy capitalize">{a.product} &middot; KES {fmt(a.amount)}</p>
                                    <span className={`og-chip ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-4 text-xs text-slate-500">
                                    <span>Score {a.score}</span>
                                    <span>Tenor {a.tenor_days}d</span>
                                    <span>Fee KES {fmt(a.fee)}</span>
                                    <span>Repay KES {fmt(a.repayment)}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
